/**
 * <biosphere-toast> — the layout's single live region, rendered by
 * snippets/biosphere-toast.liquid.
 *
 * Components never touch this element; they dispatch `biosphere:toast:show` with
 * `detail.message` (already translated) on any element — it bubbles to document, where
 * this listens. The <p role="status"> child exists from first render so announcements
 * are reliable; only the host's `hidden` attribute toggles visibility.
 */
const DEFAULT_DISPLAY_MS = 5000;

/**
 * Display time comes from the --toast-duration knob — a CSS time value
 * ("8s", "8000ms") set by the consumer on the element or any ancestor —
 * with the default applying when unset, per the knob convention.
 */
function displayDuration(element) {
  const raw = getComputedStyle(element)
    .getPropertyValue("--toast-duration")
    .trim();
  const value = Number.parseFloat(raw);
  if (Number.isNaN(value)) return DEFAULT_DISPLAY_MS;
  return raw.endsWith("ms") ? value : value * 1000;
}

class BiosphereToast extends HTMLElement {
  #message;
  #timer;
  #abort;

  connectedCallback() {
    this.#message = this.querySelector(".biosphere-toast__message");
    if (!this.#message) return;

    this.#abort = new AbortController();
    document.addEventListener(
      "biosphere:toast:show",
      (event) => {
        const message = event.detail?.message;
        if (typeof message === "string" && message !== "") this.#show(message);
      },
      { signal: this.#abort.signal },
    );

    if (!this.hidden) this.#scheduleHide();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    clearTimeout(this.#timer);
  }

  #show(message) {
    this.#message.textContent = message;
    this.hidden = false;
    this.#scheduleHide();
  }

  #scheduleHide() {
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => {
      this.hidden = true;
      // Clearing the text resets the live region for the next announcement.
      this.#message.textContent = "";
    }, displayDuration(this));
  }
}

if (!customElements.get("biosphere-toast")) {
  customElements.define("biosphere-toast", BiosphereToast);
}
